/**
 * 답변 스타일 공유 모듈 — 톤(말투) × 포맷(구조) 직교 2축. (design-20260604-answer-tone-format-orthogonal)
 *
 *   - useStyleConfig(): synthesis-format.json 전체를 편집하는 훅. 톤 탭·포맷 탭이 각각 독립 인스턴스로 호출.
 *       전체문서 저장 + baseMtime STALE_EDIT 가드. 탭 전환 시 remount → 재조회로 신선도 확보.
 *   - TONE_PRESETS / FORMAT_PRESETS: 내장 프리셋(라이브러리 초기값·추천).
 *   - DOMAIN_LABELS / RECOMMENDED_BY_DOMAIN: 카테고리 라벨·축별 추천.
 *   - ToneSample / FormatMock / FormatSample: 미리보기 시각요소(이모지 금지 — div/CSS·인라인 SVG).
 *   - SaveIcon / StyleSaveBar: 저장 UI.
 * SSOT: orchestrator synthesis-format.json (tones + formats + style_by_tool). 저장 시 ~30초 핫리로드 → 채팅 반영.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import './AiAnswerFormat.css';
import './AiAnswerStyle.css';
import './ServiceModules.css';
import {
  getSynthesisFormat, putSynthesisFormat, generateStyleEntry,
  type SynthesisFormat, type SynthesisToneEntry, type SynthesisFormatEntry, type StyleMapping,
} from '../../../api/aiFormat';

export const DOMAIN_LABELS: Record<string, string> = {
  calendar: '일정', hr: '인사', chat: '채팅', meeting: '회의록', mail: '메일',
  task: '할일', board: '게시판', incident: '장애', approval: '결재', workspace: '워크스페이스', wiki: '위키',
};

// ─── 톤(말투) 프리셋 — VOICE 전용 ───
export interface TonePreset {
  key: string; label: string; description: string;
  formality: 'formal' | 'neutral' | 'friendly';
  max_sentences: number | null;
  instructions: string[];
}
export const TONE_PRESETS: TonePreset[] = [
  { key: 'formal_detailed', label: '정중·상세', description: '격식 있고 근거를 충분히', formality: 'formal', max_sentences: null,
    instructions: ['정중하고 격식 있는 어조로 답변', '배경과 근거를 충분히 설명'] },
  { key: 'concise', label: '간결·핵심', description: '핵심만 짧게', formality: 'neutral', max_sentences: 5,
    instructions: ['핵심만 간결하게', '군더더기 없이 짧게'] },
  { key: 'friendly', label: '친근', description: '부드럽고 친근하게', formality: 'friendly', max_sentences: null,
    instructions: ['친근하고 부드러운 어조로 답변'] },
  { key: 'neutral_default', label: '기본', description: '자연스러운 일반 말투', formality: 'neutral', max_sentences: null,
    instructions: ['질문에 자연스럽고 명확하게 답변'] },
];
export const TONE_PRESET_BY_KEY: Record<string, TonePreset> = Object.fromEntries(TONE_PRESETS.map((p) => [p.key, p]));

// ─── 포맷(구조) 프리셋 — STRUCTURE 전용 ───
export interface FormatPreset {
  key: string; label: string; description: string;
  output_format: 'markdown' | 'html' | 'data_table';
  instructions: string[];
}
export const FORMAT_PRESETS: FormatPreset[] = [
  { key: 'table', label: '표 중심', description: '여러 항목을 표로', output_format: 'markdown',
    instructions: ['항목이 여러 개면 표(table)로 정리', '수치는 정렬해 비교 가능하게'] },
  { key: 'list', label: '목록', description: '핵심을 불릿으로', output_format: 'markdown',
    instructions: ['핵심을 불릿 목록으로 정리'] },
  { key: 'detailed_para', label: '서술', description: '문단으로 자세히', output_format: 'markdown',
    instructions: ['배경·근거를 포함해 문단으로 서술', '단계가 있으면 번호 목록으로'] },
  { key: 'report', label: '리포트', description: '제목·요약·본문(HTML)', output_format: 'html',
    instructions: ['제목 · 요약 · 본문 구조의 리포트 형식', '중요 수치는 강조'] },
  { key: 'data_table', label: '데이터 표 (값 고정)', description: 'LLM 합성 없이 고정 표', output_format: 'data_table',
    instructions: [] },
  { key: 'diagram', label: '흐름도 (DAG)', description: '노드·간선 흐름도(mermaid)', output_format: 'markdown',
    instructions: ['Mermaid flowchart(graph TD 또는 LR) 문법으로 흐름도를 먼저 제시', '노드와 방향 간선으로 관계·순서를 표현', '흐름도 아래에 핵심 노드·연결을 간단히 설명'] },
  { key: 'gantt', label: '간트 차트', description: '일정·기간 간트(mermaid)', output_format: 'markdown',
    instructions: ['Mermaid gantt 문법으로 일정/기간을 간트 차트로 작성', '작업명·시작일·기간(또는 종료일)을 명시', '마일스톤이 있으면 milestone 으로 표시'] },
  { key: 'mindmap', label: '마인드맵', description: '중심·가지 마인드맵(mermaid)', output_format: 'markdown',
    instructions: ['Mermaid mindmap 문법으로 중심 주제와 가지를 계층으로 작성', '상위→하위 개념을 들여쓰기 계층으로 표현', '핵심 키워드 위주로 간결하게'] },
  { key: 'sequence', label: '시퀀스 다이어그램', description: '참여자 간 메시지 순서(mermaid)', output_format: 'markdown',
    instructions: ['Mermaid sequenceDiagram 문법으로 참여자(participant) 간 메시지 순서를 작성', '요청→응답 화살표로 상호작용을 시간 순서로 표현', '핵심 단계 위주로 간결하게'] },
  { key: 'pie', label: '파이 차트', description: '비율·구성비 원형 차트(mermaid)', output_format: 'markdown',
    instructions: ['Mermaid pie 문법으로 항목별 비율을 원형 차트로 작성', '각 항목명과 수치(비율)를 명시', '합계가 100%가 되도록 구성'] },
];
export const FORMAT_PRESET_BY_KEY: Record<string, FormatPreset> = Object.fromEntries(FORMAT_PRESETS.map((p) => [p.key, p]));

// ─── 색상 팔레트 프리셋 (차트·다이어그램·HTML 강조용) ───
export interface ColorPalette { key: string; label: string; colors: string[] }
export const COLOR_PALETTES: ColorPalette[] = [
  { key: 'indigo', label: '인디고', colors: ['#6366f1', '#818cf8', '#a5b4fc', '#c7d2fe', '#4338ca'] },
  { key: 'ocean', label: '오션', colors: ['#0ea5e9', '#22d3ee', '#2563eb', '#14b8a6', '#0891b2'] },
  { key: 'forest', label: '포레스트', colors: ['#16a34a', '#65a30d', '#22c55e', '#0d9488', '#84cc16'] },
  { key: 'sunset', label: '선셋', colors: ['#f97316', '#f59e0b', '#ef4444', '#ec4899', '#d97706'] },
  { key: 'vivid', label: '비비드', colors: ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#a855f7'] },
  { key: 'mono', label: '모노', colors: ['#334155', '#64748b', '#94a3b8', '#cbd5e1', '#475569'] },
];
export const COLOR_PALETTE_BY_KEY: Record<string, ColorPalette> = Object.fromEntries(COLOR_PALETTES.map((p) => [p.key, p]));

// 카테고리별 축 추천 — (톤, 포맷).
export const RECOMMENDED_BY_DOMAIN: Record<string, { tone: string; format: string }> = {
  calendar: { tone: 'concise', format: 'table' },
  hr: { tone: 'formal_detailed', format: 'table' },
  chat: { tone: 'concise', format: 'table' },
  meeting: { tone: 'formal_detailed', format: 'report' },
  approval: { tone: 'formal_detailed', format: 'report' },
  incident: { tone: 'concise', format: 'report' },
  board: { tone: 'neutral_default', format: 'detailed_para' },
  workspace: { tone: 'neutral_default', format: 'detailed_para' },
  wiki: { tone: 'neutral_default', format: 'detailed_para' },
  task: { tone: 'concise', format: 'list' },
  mail: { tone: 'concise', format: 'list' },
};

export interface ToolLite { name: string; description?: string }

function clone<T>(v: T): T { return JSON.parse(JSON.stringify(v)); }
function emptyFormat(): SynthesisFormat {
  return { version: 3, tones: {}, formats: {}, style_by_tool: {}, tone_by_tool: {}, format_hints: {}, common: { language: 'ko' } };
}

// ─────────────────────────────────────────────────────────────────────────────
// 훅 — synthesis-format.json 전체 편집. 톤/포맷 탭이 각각 독립 호출.
// ─────────────────────────────────────────────────────────────────────────────
export interface StyleApi {
  fmt: SynthesisFormat | null;
  loading: boolean; saving: boolean; error: string | null; notice: string | null; dirty: boolean;
  reload: () => Promise<void>;
  saveAll: () => Promise<void>;
  // 카테고리 단위 저장 — 해당 도메인 매핑(+공유 라이브러리)만 저장, 다른 카테고리 변경분 보존.
  categoryDirty: (domain: string) => boolean;
  saveCategory: (domain: string) => Promise<void>;
  savingCategory: string | null;
  update: (mut: (f: SynthesisFormat) => void) => void;

  // 톤 라이브러리
  addToneFromPreset: (presetKey: string) => string;
  addCustomTone: () => string;
  addToneEntry: (entry: SynthesisToneEntry) => string;
  editTone: (key: string, mut: (t: SynthesisToneEntry) => void) => void;
  removeTone: (key: string) => void;

  // 포맷 라이브러리
  addFormatFromPreset: (presetKey: string) => string;
  addCustomFormat: () => string;
  addFormatEntry: (entry: SynthesisFormatEntry) => string;
  editFormat: (key: string, mut: (f: SynthesisFormatEntry) => void) => void;
  removeFormat: (key: string) => void;

  // 카테고리/도구 매핑 (style_by_tool)
  styleForCategory: (domain: string) => StyleMapping;
  setCategoryTone: (domain: string, toneKey: string) => void;
  setCategoryFormat: (domain: string, formatKey: string) => void;
  styleForTool: (toolName: string) => StyleMapping;
  setToolTone: (toolName: string, toneKey: string) => void;
  setToolFormat: (toolName: string, formatKey: string) => void;
  effectiveTone: (domain: string, toolName: string) => { key: string | undefined; source: '개별' | '기본' | '미설정' };
  effectiveFormat: (domain: string, toolName: string) => { key: string | undefined; source: '개별' | '기본' | '미설정' };

  // 마이그레이션 (legacy tone_by_tool → style_by_tool + formats)
  hasLegacy: boolean;
  importLegacy: () => void;
}

export function useStyleConfig(): StyleApi {
  const [fmt, setFmt] = useState<SynthesisFormat | null>(null);
  const [baseMtime, setBaseMtime] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingCategory, setSavingCategory] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null); setNotice(null);
    try {
      const r = await getSynthesisFormat();
      const c = r.content ?? emptyFormat();
      if (!c.tones) c.tones = {};
      if (!c.formats) c.formats = {};
      if (!c.style_by_tool) c.style_by_tool = {};
      if (!c.tone_by_tool) c.tone_by_tool = {};
      if (!c.format_hints) c.format_hints = {};
      if (!c.common) c.common = { language: 'ko' };
      setFmt(c); setBaseMtime(r.mtime); setLoaded(JSON.stringify(c));
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.response?.data?.error ?? '설정을 불러오지 못했습니다.');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  const dirty = !!fmt && JSON.stringify(fmt) !== loaded;

  function update(mut: (f: SynthesisFormat) => void) {
    setFmt((prev) => { if (!prev) return prev; const next = clone(prev); mut(next); return next; });
    setNotice(null);
  }

  // 고유 키 생성 (custom).
  function freshKey(existing: Record<string, unknown>, base: string): string {
    let i = 1; let k = `${base}_${i}`;
    while (existing[k]) { i += 1; k = `${base}_${i}`; }
    return k;
  }

  // ── 톤 라이브러리 ──
  function addToneFromPreset(presetKey: string): string {
    const p = TONE_PRESET_BY_KEY[presetKey]; if (!p) return '';
    update((f) => {
      if (!f.tones[p.key]) {
        f.tones[p.key] = { description: p.label, instructions: [...p.instructions], max_sentences: p.max_sentences, formality: p.formality };
      }
    });
    return p.key;
  }
  function addCustomTone(): string {
    const key = freshKey(fmt?.tones ?? {}, 'tone');
    update((f) => { f.tones[key] = { description: '새 톤', instructions: ['정중하게 답변'], formality: 'neutral', max_sentences: null }; });
    return key;
  }
  // AI 생성 등 외부에서 만든 톤 엔트리를 라이브러리에 추가(고유 키 부여).
  function addToneEntry(entry: SynthesisToneEntry): string {
    const key = freshKey(fmt?.tones ?? {}, 'tone');
    update((f) => {
      f.tones[key] = {
        description: entry.description || 'AI 생성 톤',
        instructions: Array.isArray(entry.instructions) ? entry.instructions : [],
        max_sentences: entry.max_sentences ?? null,
        formality: entry.formality ?? 'neutral',
      };
    });
    return key;
  }
  function editTone(key: string, mut: (t: SynthesisToneEntry) => void) {
    update((f) => { const t = f.tones[key]; if (t) mut(t); });
  }
  function removeTone(key: string) {
    update((f) => {
      delete f.tones[key];
      for (const m of Object.values(f.style_by_tool ?? {})) { if (m.tone === key) delete m.tone; }
    });
  }

  // ── 포맷 라이브러리 ──
  function addFormatFromPreset(presetKey: string): string {
    const p = FORMAT_PRESET_BY_KEY[presetKey]; if (!p) return '';
    update((f) => {
      if (!f.formats) f.formats = {};
      if (!f.formats[p.key]) {
        f.formats[p.key] = { description: p.label, instructions: [...p.instructions], output_format: p.output_format };
      }
    });
    return p.key;
  }
  function addCustomFormat(): string {
    const key = freshKey(fmt?.formats ?? {}, 'format');
    update((f) => { if (!f.formats) f.formats = {}; f.formats[key] = { description: '새 포맷', instructions: ['표로 정리'], output_format: 'markdown' }; });
    return key;
  }
  // AI 생성 등 외부에서 만든 포맷 엔트리를 라이브러리에 추가(고유 키 부여).
  function addFormatEntry(entry: SynthesisFormatEntry): string {
    const key = freshKey(fmt?.formats ?? {}, 'format');
    update((f) => {
      if (!f.formats) f.formats = {};
      f.formats[key] = {
        description: entry.description || 'AI 생성 포맷',
        instructions: Array.isArray(entry.instructions) ? entry.instructions : [],
        output_format: entry.output_format ?? 'markdown',
        ...(entry.mock ? { mock: entry.mock } : {}),
      };
    });
    return key;
  }
  function editFormat(key: string, mut: (e: SynthesisFormatEntry) => void) {
    update((f) => { if (!f.formats) f.formats = {}; const e = f.formats[key]; if (e) mut(e); });
  }
  function removeFormat(key: string) {
    update((f) => {
      if (f.formats) delete f.formats[key];
      for (const m of Object.values(f.style_by_tool ?? {})) { if (m.format === key) delete m.format; }
    });
  }

  // ── 매핑 (style_by_tool) ──
  function setMapping(f: SynthesisFormat, mapKey: string, patch: Partial<StyleMapping>) {
    if (!f.style_by_tool) f.style_by_tool = {};
    const cur: StyleMapping = { ...(f.style_by_tool[mapKey] ?? {}) };
    if (patch.tone !== undefined) { if (patch.tone === '') delete cur.tone; else cur.tone = patch.tone; }
    if (patch.format !== undefined) { if (patch.format === '') delete cur.format; else cur.format = patch.format; }
    if (cur.tone === undefined && cur.format === undefined) delete f.style_by_tool[mapKey];
    else f.style_by_tool[mapKey] = cur;
  }
  const prefixOf = (domain: string) => `${domain}.`;
  const styleForCategory = (domain: string): StyleMapping => fmt?.style_by_tool?.[prefixOf(domain)] ?? {};
  const styleForTool = (toolName: string): StyleMapping => fmt?.style_by_tool?.[toolName] ?? {};
  const setCategoryTone = (domain: string, toneKey: string) => update((f) => setMapping(f, prefixOf(domain), { tone: toneKey }));
  const setCategoryFormat = (domain: string, formatKey: string) => update((f) => setMapping(f, prefixOf(domain), { format: formatKey }));
  const setToolTone = (toolName: string, toneKey: string) => update((f) => setMapping(f, toolName, { tone: toneKey }));
  const setToolFormat = (toolName: string, formatKey: string) => update((f) => setMapping(f, toolName, { format: formatKey }));

  function effectiveTone(domain: string, toolName: string) {
    const ov = styleForTool(toolName).tone;
    if (ov) return { key: ov, source: '개별' as const };
    const cat = styleForCategory(domain).tone;
    if (cat) return { key: cat, source: '기본' as const };
    return { key: undefined, source: '미설정' as const };
  }
  function effectiveFormat(domain: string, toolName: string) {
    const ov = styleForTool(toolName).format;
    if (ov) return { key: ov, source: '개별' as const };
    const cat = styleForCategory(domain).format;
    if (cat) return { key: cat, source: '기본' as const };
    return { key: undefined, source: '미설정' as const };
  }

  // ── 마이그레이션: legacy tone_by_tool(+combined tones) → style_by_tool + formats ──
  const hasLegacy = !!fmt && Object.keys(fmt.tone_by_tool ?? {}).length > 0
    && Object.keys(fmt.style_by_tool ?? {}).length === 0;
  function importLegacy() {
    update((f) => {
      if (!f.formats) f.formats = {};
      if (!f.style_by_tool) f.style_by_tool = {};
      for (const [prefix, toneKey] of Object.entries(f.tone_by_tool ?? {})) {
        const t = f.tones[toneKey];
        if (!t) continue;
        // 출력형식 → 포맷 라이브러리 엔트리 파생.
        const of = t.output_format ?? 'markdown';
        const fmtKey = of === 'html' ? 'report' : of === 'data_table' ? 'data_table' : 'table';
        if (!f.formats[fmtKey]) {
          const p = FORMAT_PRESET_BY_KEY[fmtKey];
          f.formats[fmtKey] = p
            ? { description: p.label, instructions: [...p.instructions], output_format: p.output_format }
            : { description: fmtKey, instructions: [], output_format: of };
        }
        // 기존 톤(combined)은 voice 로 그대로 사용. 매핑 = (toneKey, fmtKey).
        f.style_by_tool[prefix] = { tone: toneKey, format: fmtKey };
      }
    });
  }

  async function saveAll() {
    if (!fmt || !dirty || saving) return;
    setSaving(true); setError(null); setNotice(null);
    try {
      const res = await putSynthesisFormat(fmt, baseMtime);
      if (res?.ok === false || res?.error) { setError(res.message ?? res.error ?? '저장 실패'); return; }
      setBaseMtime(res.mtime ?? baseMtime);
      setLoaded(JSON.stringify(fmt));
      setNotice('저장되었습니다. 최대 30초 내 채팅 답변에 반영됩니다.');
    } catch (e: any) {
      const code = e?.response?.data?.error;
      if (code === 'STALE_EDIT') setError('다른 관리자가 먼저 저장했습니다. 새로고침(되돌리기) 후 다시 시도하세요.');
      else setError(e?.response?.data?.message ?? e?.response?.data?.error ?? '저장에 실패했습니다.');
    } finally { setSaving(false); }
  }

  // ── 카테고리 단위 저장 ── 한 도메인의 style_by_tool(prefix + 도구 override)만 저장.
  const catKeys = (sb: Record<string, StyleMapping> | undefined, domain: string) =>
    Object.keys(sb ?? {}).filter((k) => k === `${domain}.` || k.startsWith(`${domain}.`));
  function categorySlice(f: SynthesisFormat, domain: string): string {
    const sb = f.style_by_tool ?? {};
    return JSON.stringify(catKeys(sb, domain).sort().map((k) => [k, sb[k]]));
  }
  function categoryDirty(domain: string): boolean {
    if (!fmt || !loaded) return false;
    let base: SynthesisFormat;
    try { base = JSON.parse(loaded); } catch { return false; }
    return categorySlice(fmt, domain) !== categorySlice(base, domain);
  }
  async function saveCategory(domain: string) {
    if (!fmt || savingCategory) return;
    let base: SynthesisFormat;
    try { base = JSON.parse(loaded); } catch { base = clone(fmt); }
    const payload = clone(base);
    // 공유 라이브러리·전역은 현재값 반영(이 카테고리가 새 톤/포맷을 참조할 수 있음).
    payload.tones = fmt.tones; payload.formats = fmt.formats;
    payload.common = fmt.common; payload.format_hints = fmt.format_hints;
    if (!payload.style_by_tool) payload.style_by_tool = {};
    for (const k of catKeys(base.style_by_tool, domain)) delete payload.style_by_tool[k];
    for (const k of catKeys(fmt.style_by_tool, domain)) payload.style_by_tool[k] = fmt.style_by_tool![k]!;
    setSavingCategory(domain); setError(null); setNotice(null);
    try {
      const res = await putSynthesisFormat(payload, baseMtime);
      if (res?.ok === false || res?.error) { setError(res.message ?? res.error ?? '저장 실패'); return; }
      setBaseMtime(res.mtime ?? baseMtime);
      setLoaded(JSON.stringify(payload)); // 이 카테고리만 clean — 다른 카테고리 변경분 유지
      setNotice(`'${DOMAIN_LABELS[domain] ?? domain}' 카테고리가 저장되었습니다. 최대 30초 내 채팅 반영.`);
    } catch (e: any) {
      const code = e?.response?.data?.error;
      if (code === 'STALE_EDIT') setError('다른 관리자가 먼저 저장했습니다. 새로고침(되돌리기) 후 다시 시도하세요.');
      else setError(e?.response?.data?.message ?? e?.response?.data?.error ?? '저장에 실패했습니다.');
    } finally { setSavingCategory(null); }
  }

  return {
    fmt, loading, saving, error, notice, dirty, reload, saveAll, update,
    categoryDirty, saveCategory, savingCategory,
    addToneFromPreset, addCustomTone, addToneEntry, editTone, removeTone,
    addFormatFromPreset, addCustomFormat, addFormatEntry, editFormat, removeFormat,
    styleForCategory, setCategoryTone, setCategoryFormat, styleForTool, setToolTone, setToolFormat,
    effectiveTone, effectiveFormat,
    hasLegacy, importLegacy,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// 시각 요소 (이모지 금지)
// ─────────────────────────────────────────────────────────────────────────────

/** 포맷 미니목업 — 답변이 어떤 형태로 보일지. */
export function FormatMock({ kind }: { kind: string }) {
  switch (kind) {
    case 'list': return <div className="pm pm-list"><span/><span/><span/></div>;
    case 'table': return <div className="pm pm-table"><i/><i/><i/><i/><i/><i/></div>;
    case 'detailed_para': return <div className="pm pm-para"><u/><u/><u/><u/></div>;
    case 'report': return <div className="pm pm-report"><strong/><u/><u/><em/></div>;
    case 'data_table': return <div className="pm pm-table"><i/><i/><i/><i/><i/><i/></div>;
    case 'diagram': return (
      <svg className="pm pm-diagram" viewBox="0 0 80 44" preserveAspectRatio="xMidYMid meet" aria-hidden>
        <g className="pm-diagram__edge" fill="none" strokeWidth="1.5">
          <path d="M20 12 H34" /><path d="M20 32 H34" /><path d="M54 22 H66" />
          <path d="M48 16 L54 21" /><path d="M48 28 L54 23" />
        </g>
        <rect className="pm-diagram__node is-root" x="6" y="6" width="14" height="12" rx="2" />
        <rect className="pm-diagram__node" x="34" y="6" width="14" height="12" rx="2" />
        <rect className="pm-diagram__node" x="34" y="26" width="14" height="12" rx="2" />
        <rect className="pm-diagram__node is-leaf" x="66" y="16" width="12" height="12" rx="2" />
      </svg>
    );
    case 'gantt': return (
      <svg className="pm pm-gantt" viewBox="0 0 80 44" preserveAspectRatio="xMidYMid meet" aria-hidden>
        <rect className="pm-gantt__bar is-root" x="8" y="7" width="34" height="7" rx="2" />
        <rect className="pm-gantt__bar" x="24" y="19" width="40" height="7" rx="2" />
        <rect className="pm-gantt__bar" x="16" y="31" width="28" height="7" rx="2" />
      </svg>
    );
    case 'mindmap': return (
      <svg className="pm pm-mindmap" viewBox="0 0 80 44" preserveAspectRatio="xMidYMid meet" aria-hidden>
        <g className="pm-mindmap__edge" fill="none" strokeWidth="1.5">
          <path d="M40 22 L18 10" /><path d="M40 22 L18 34" /><path d="M40 22 L64 12" /><path d="M40 22 L64 32" />
        </g>
        <circle className="pm-mindmap__node is-root" cx="40" cy="22" r="6" />
        <circle className="pm-mindmap__node" cx="15" cy="10" r="4" /><circle className="pm-mindmap__node" cx="15" cy="34" r="4" />
        <circle className="pm-mindmap__node" cx="66" cy="12" r="4" /><circle className="pm-mindmap__node" cx="66" cy="32" r="4" />
      </svg>
    );
    case 'sequence': return (
      <svg className="pm pm-seq" viewBox="0 0 80 44" preserveAspectRatio="xMidYMid meet" aria-hidden>
        <rect className="pm-seq__node" x="12" y="4" width="20" height="8" rx="2" />
        <rect className="pm-seq__node" x="48" y="4" width="20" height="8" rx="2" />
        <g className="pm-seq__life" strokeWidth="1.2"><line x1="22" y1="12" x2="22" y2="40" /><line x1="58" y1="12" x2="58" y2="40" /></g>
        <g className="pm-seq__msg" strokeWidth="1.5"><line x1="22" y1="22" x2="58" y2="22" /><line x1="58" y1="32" x2="22" y2="32" /></g>
      </svg>
    );
    case 'pie': return (
      <svg className="pm pm-pie" viewBox="0 0 80 44" preserveAspectRatio="xMidYMid meet" aria-hidden>
        <g transform="rotate(-90 40 22)" fill="none" strokeWidth="11">
          <circle className="pm-pie__s1" cx="40" cy="22" r="15" strokeDasharray="47.1 94.2" />
          <circle className="pm-pie__s2" cx="40" cy="22" r="15" strokeDasharray="28.3 94.2" strokeDashoffset="-47.1" />
          <circle className="pm-pie__s3" cx="40" cy="22" r="15" strokeDasharray="18.8 94.2" strokeDashoffset="-75.4" />
        </g>
      </svg>
    );
    case 'none': return <div className="pm pm-none"><span>없음</span></div>;
    default: return <div className="pm pm-plain"><u/><u/></div>;
  }
}

/** 포맷별 실제 답변 예시 (채팅 렌더와 동일 형태, 샘플 고정). */
export function FormatSample({ kind }: { kind: string }) {
  switch (kind) {
    case 'list':
      return (<div className="aiaf-sample"><p>다음 주 일정은 아래와 같습니다.</p><ul>
        <li><strong>월 10:00</strong> 팀 주간 회의</li><li><strong>수 14:00</strong> 고객사 미팅</li><li><strong>금 16:00</strong> 스프린트 회고</li></ul></div>);
    case 'table':
      return (<div className="aiaf-sample"><p>요청하신 일정을 표로 정리했습니다.</p><table>
        <thead><tr><th>일정</th><th>시간</th><th>장소</th></tr></thead>
        <tbody><tr><td>팀 회의</td><td>월 10:00</td><td>3F 회의실</td></tr><tr><td>고객 미팅</td><td>수 14:00</td><td>온라인</td></tr></tbody></table></div>);
    case 'detailed_para':
      return (<div className="aiaf-sample"><p>다음 주에는 총 3건의 일정이 예정되어 있습니다. 월요일 오전 10시 팀 주간 회의에서 지난 스프린트 결과를 공유하고 이번 주 우선순위를 정합니다.</p><p>수요일 오후 2시 고객사 미팅은 온라인으로 진행되며, 금요일 오후 4시에는 회고를 통해 개선점을 도출합니다.</p></div>);
    case 'report':
      return (<div className="aiaf-sample"><p className="s-title">주간 일정 리포트</p><p className="s-sub">요약 — 다음 주 일정 <strong>3건</strong>, 외부 미팅 <strong>1건</strong>.</p><p>월요일 팀 회의를 시작으로 수요일 고객 미팅, 금요일 회고가 이어집니다. 외부 일정 비중은 <mark>33%</mark>.</p></div>);
    case 'data_table':
      return (<div className="aiaf-sample"><p className="aiaf-preview__note"><b>값 고정(결정적)</b> — LLM 합성 없이 도구가 반환한 항목·값을 항상 같은 표로 렌더합니다.</p><table>
        <thead><tr><th>필드</th><th>값</th></tr></thead><tbody><tr><td>이름</td><td>홍길동</td></tr><tr><td>부서</td><td>영업팀</td></tr></tbody></table></div>);
    case 'diagram':
      return (<div className="aiaf-sample"><p>승인 절차 흐름을 도식화했습니다.</p>
        <svg className="aiv-dag" viewBox="0 0 320 120" preserveAspectRatio="xMidYMid meet" role="img" aria-label="흐름도 예시">
          <defs><marker id="aiv-arrow" markerWidth="7" markerHeight="7" refX="6" refY="3" orient="auto">
            <path d="M0 0 L6 3 L0 6 z" fill="#94a3b8" /></marker></defs>
          <g fill="none" stroke="#94a3b8" strokeWidth="1.5" markerEnd="url(#aiv-arrow)">
            <path d="M70 34 H108" /><path d="M178 34 H216" /><path d="M124 52 V74 H124" />
            <path d="M124 52 L124 74" /><path d="M178 86 H216 V52" />
          </g>
          <g className="aiv-dag__node"><rect x="12" y="20" width="58" height="28" rx="5" /><text x="41" y="38">기안</text></g>
          <g className="aiv-dag__node"><rect x="108" y="20" width="62" height="28" rx="5" /><text x="139" y="38">팀장 검토</text></g>
          <g className="aiv-dag__node is-leaf"><rect x="216" y="20" width="62" height="28" rx="5" /><text x="247" y="38">승인 완료</text></g>
          <g className="aiv-dag__node"><rect x="92" y="74" width="64" height="28" rx="5" /><text x="124" y="92">반려</text></g>
        </svg>
        <p className="aiaf-preview__note">채팅에서는 <b>Mermaid 흐름도</b>로 렌더됩니다(```mermaid graph LR …```).</p></div>);
    case 'gantt':
      return (<div className="aiaf-sample"><p>프로젝트 일정을 간트 차트로 정리했습니다.</p>
        <svg className="aiv-gantt-ex" viewBox="0 0 320 120" preserveAspectRatio="xMidYMid meet" role="img" aria-label="간트 예시">
          <g className="aiv-gantt-ex__grid" stroke="#eef2f6" strokeWidth="1">
            <line x1="96" y1="8" x2="96" y2="112" /><line x1="156" y1="8" x2="156" y2="112" /><line x1="216" y1="8" x2="216" y2="112" /><line x1="276" y1="8" x2="276" y2="112" /></g>
          <g className="aiv-gantt-ex__lbl"><text x="6" y="26">기획</text><text x="6" y="52">디자인</text><text x="6" y="78">개발</text><text x="6" y="104">검수</text></g>
          <g className="aiv-gantt-ex__bar"><rect x="96" y="18" width="60" height="14" rx="3" /><rect x="140" y="44" width="76" height="14" rx="3" /><rect x="180" y="70" width="96" height="14" rx="3" /><rect x="252" y="96" width="48" height="14" rx="3" /></g>
        </svg>
        <p className="aiaf-preview__note">채팅에서는 <b>Mermaid gantt</b> 로 렌더됩니다(```mermaid gantt …```).</p></div>);
    case 'mindmap':
      return (<div className="aiaf-sample"><p>핵심 주제를 마인드맵으로 정리했습니다.</p>
        <svg className="aiv-mindmap-ex" viewBox="0 0 320 140" preserveAspectRatio="xMidYMid meet" role="img" aria-label="마인드맵 예시">
          <g fill="none" stroke="#c7d2fe" strokeWidth="1.5">
            <path d="M160 70 C120 70 110 36 86 30" /><path d="M160 70 C120 70 110 104 86 110" /><path d="M160 70 C210 70 220 34 248 28" /><path d="M160 70 C210 70 220 106 248 112" /></g>
          <g className="aiv-mindmap-ex__leaf"><rect x="20" y="18" width="66" height="24" rx="12" /><text x="53" y="30">시장</text>
            <rect x="20" y="98" width="66" height="24" rx="12" /><text x="53" y="110">조직</text>
            <rect x="248" y="16" width="66" height="24" rx="12" /><text x="281" y="28">제품</text>
            <rect x="248" y="100" width="66" height="24" rx="12" /><text x="281" y="112">재무</text></g>
          <g className="aiv-mindmap-ex__root"><rect x="126" y="56" width="68" height="28" rx="14" /><text x="160" y="70">사업계획</text></g>
        </svg>
        <p className="aiaf-preview__note">채팅에서는 <b>Mermaid mindmap</b> 으로 렌더됩니다(```mermaid mindmap …```).</p></div>);
    case 'sequence':
      return (<div className="aiaf-sample"><p>요청 처리 상호작용을 시퀀스로 정리했습니다.</p>
        <svg className="aiv-seq-ex" viewBox="0 0 320 130" preserveAspectRatio="xMidYMid meet" role="img" aria-label="시퀀스 예시">
          <defs><marker id="aiv-seqarrow" markerWidth="7" markerHeight="7" refX="6" refY="3" orient="auto"><path d="M0 0 L6 3 L0 6 z" fill="#94a3b8" /></marker></defs>
          <g className="aiv-seq-ex__actor"><rect x="32" y="8" width="76" height="24" rx="5" /><text x="70" y="20">사용자</text>
            <rect x="212" y="8" width="76" height="24" rx="5" /><text x="250" y="20">시스템</text></g>
          <g className="aiv-seq-ex__life" stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="3 3"><line x1="70" y1="32" x2="70" y2="122" /><line x1="250" y1="32" x2="250" y2="122" /></g>
          <g className="aiv-seq-ex__msg" stroke="#94a3b8" strokeWidth="1.5" markerEnd="url(#aiv-seqarrow)"><line x1="70" y1="52" x2="250" y2="52" /><line x1="250" y1="82" x2="70" y2="82" /><line x1="70" y1="112" x2="250" y2="112" /></g>
          <g className="aiv-seq-ex__lbl"><text x="160" y="48">요청 전송</text><text x="160" y="78">결과 응답</text><text x="160" y="108">완료 확인</text></g>
        </svg>
        <p className="aiaf-preview__note">채팅에서는 <b>Mermaid sequenceDiagram</b> 으로 렌더됩니다.</p></div>);
    case 'pie':
      return (<div className="aiaf-sample"><p>구성 비율을 파이 차트로 정리했습니다.</p>
        <svg className="aiv-pie-ex" viewBox="0 0 320 130" preserveAspectRatio="xMidYMid meet" role="img" aria-label="파이 예시">
          <g transform="rotate(-90 80 65)" fill="none" strokeWidth="30">
            <circle className="aiv-pie-ex__s1" cx="80" cy="65" r="40" strokeDasharray="125.7 251.3" />
            <circle className="aiv-pie-ex__s2" cx="80" cy="65" r="40" strokeDasharray="75.4 251.3" strokeDashoffset="-125.7" />
            <circle className="aiv-pie-ex__s3" cx="80" cy="65" r="40" strokeDasharray="50.3 251.3" strokeDashoffset="-201.1" />
          </g>
          <g className="aiv-pie-ex__lg"><rect className="aiv-pie-ex__s1f" x="170" y="34" width="12" height="12" rx="2" /><text x="190" y="44">개발 50%</text>
            <rect className="aiv-pie-ex__s2f" x="170" y="60" width="12" height="12" rx="2" /><text x="190" y="70">운영 30%</text>
            <rect className="aiv-pie-ex__s3f" x="170" y="86" width="12" height="12" rx="2" /><text x="190" y="96">기타 20%</text></g>
        </svg>
        <p className="aiaf-preview__note">채팅에서는 <b>Mermaid pie</b> 로 렌더됩니다(```mermaid pie …```).</p></div>);
    case 'none':
      return <div className="aiaf-sample aiaf-sample--muted"><p>미설정 — 시스템 기본 형식으로 답변합니다.</p></div>;
    default:
      return <div className="aiaf-sample"><p>다음 주에는 월요일 팀 회의, 수요일 고객 미팅, 금요일 회고가 예정되어 있어요.</p></div>;
  }
}

/** 톤별 말투 예시 — 동일 내용을 격식만 달리. */
export function ToneSample({ formality }: { formality?: string }) {
  switch (formality) {
    case 'formal':
      return <div className="aiaf-sample"><p>요청하신 내용을 확인하였습니다. 다음 주 일정은 총 3건으로 정리되며, 세부 사항을 아래와 같이 안내드립니다.</p></div>;
    case 'friendly':
      return <div className="aiaf-sample"><p>네, 확인했어요! 다음 주 일정은 모두 3건이에요. 아래에서 자세히 살펴볼게요.</p></div>;
    default:
      return <div className="aiaf-sample"><p>다음 주 일정은 총 3건입니다. 아래에 정리했습니다.</p></div>;
  }
}

/** 저장 아이콘 (인라인 SVG). */
export function SaveIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flex: 'none' }}>
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" />
    </svg>
  );
}

/** 저장 바 — dirty 시 sticky 강조. */
export function StyleSaveBar({ api, label }: { api: StyleApi; label: string }) {
  const { error, notice, dirty, saving, reload, saveAll } = api;
  return (
    <div className={`aiaf-savebar${dirty ? ' is-dirty' : ''}`}>
      {error && <div className="settings-error" role="alert">{error}</div>}
      {notice && <div className="aiaf-notice" role="status">{notice}</div>}
      <div className="aiaf-savebar__row">
        <span className="aiaf-savebar__status">
          {dirty
            ? <><span className="aiaf-savebar__dot" /> 저장되지 않은 변경사항이 있습니다 — 저장해야 채팅에 반영됩니다</>
            : <>모든 변경사항이 저장되었습니다</>}
        </span>
        <div className="aiaf-actions">
          <button type="button" className="settings-btn" disabled={!dirty || saving} onClick={() => void reload()}>되돌리기</button>
          <button type="button" className="settings-btn settings-btn--primary" disabled={!dirty || saving} onClick={() => void saveAll()}>
            <SaveIcon /> {saving ? '저장 중…' : label}
          </button>
        </div>
      </div>
    </div>
  );
}

export function Field(props: { label: ReactNode; children: ReactNode }) {
  return <label className="aiaf-fld">{props.label}{props.children}</label>;
}

/** AI 프리셋 생성 패널 — 자연어 brief → LLM 단일 톤/포맷 엔트리 → 미리보기 → 라이브러리 추가. */
export function AiGeneratePanel({ kind, onAccept }: { kind: 'tone' | 'format'; onAccept: (entry: SynthesisToneEntry & SynthesisFormatEntry) => void }) {
  const [open, setOpen] = useState(false);
  const [brief, setBrief] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [proposal, setProposal] = useState<{ entry: SynthesisToneEntry & SynthesisFormatEntry; rationale?: string } | null>(null);

  async function gen() {
    const b = brief.trim();
    if (!b || loading) return;
    setLoading(true); setErr(null); setProposal(null);
    try {
      const r = await generateStyleEntry(kind, b);
      if (r.ok && r.entry) setProposal({ entry: r.entry, rationale: r.rationale });
      else setErr(r.message || r.validation_error || r.error || '생성에 실패했습니다.');
    } catch (e: any) {
      setErr(e?.response?.data?.message ?? e?.response?.data?.error ?? '생성 실패 (시간 초과이거나 AI Provider 미설정일 수 있습니다).');
    } finally { setLoading(false); }
  }
  const label = kind === 'tone' ? '톤' : '포맷';
  const ph = kind === 'tone'
    ? '예: 임원 보고용으로 아주 정중하고 핵심만 3문장 이내로'
    : '예: 항목을 표로 정리하고 합계 행을 강조, 중요 수치는 굵게';

  return (
    <div className="aiaf-aigen">
      <button type="button" className={`aiaf-aigen__toggle${open ? ' is-open' : ''}`} onClick={() => setOpen((v) => !v)}>
        <SparkIcon /> AI로 {label} 생성
      </button>
      {open && (
        <div className="aiaf-aigen__body">
          <textarea className="aiaf-textarea" rows={2} value={brief} placeholder={ph}
            onChange={(e) => setBrief(e.target.value)} />
          <div className="aiaf-aigen__row">
            <button type="button" className="settings-btn settings-btn--primary" disabled={loading || !brief.trim()} onClick={() => void gen()}>
              {loading ? '생성 중…' : '생성'}
            </button>
            {loading && <span className="aiaf-aigen__hint">AI가 작성 중입니다… (최대 1분)</span>}
          </div>
          {err && <div className="settings-error" role="alert">{err}</div>}
          {proposal && (
            <div className="aiaf-aigen__proposal">
              <div className="aiaf-detail__sec-lbl">제안: {proposal.entry.description || `(이름 없음)`}</div>
              {kind === 'tone' ? <ToneVizSample tone={proposal.entry} /> : <FormatVizSample format={proposal.entry} />}
              {proposal.rationale && <p className="aiaf-aigen__rationale">{proposal.rationale}</p>}
              <div className="aiaf-aigen__row">
                <button type="button" className="settings-btn settings-btn--primary"
                  onClick={() => { onAccept(proposal.entry); setProposal(null); setBrief(''); setOpen(false); }}>
                  라이브러리에 추가
                </button>
                <button type="button" className="settings-btn" onClick={() => void gen()} disabled={loading}>다시 생성</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** 반짝임 아이콘 (인라인 SVG — 이모지 금지). */
function SparkIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flex: 'none' }}>
      <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 상세화 — 시각화 프리미티브 + 결합 예제 (design §4 상세화)
// ─────────────────────────────────────────────────────────────────────────────

const FORMALITY_IDX: Record<string, number> = { formal: 0, neutral: 1, friendly: 2 };
const FORMALITY_TEXT = ['정중', '중립', '친근'];

/** 격식 3단 게이지. */
export function FormalityGauge({ formality }: { formality?: string }) {
  const idx = FORMALITY_IDX[formality ?? 'neutral'] ?? 1;
  return (
    <div className="aiv-gauge">
      {[0, 1, 2].map((i) => <span key={i} className={`aiv-gauge__dot${i === idx ? ' is-on' : ''}`} />)}
      <span className="aiv-gauge__lbl">{FORMALITY_TEXT[idx]}</span>
    </div>
  );
}

/** 답변 길이 바 (max_sentences). null=무제한. */
export function LengthBar({ maxSentences }: { maxSentences?: number | null }) {
  const max = 10;
  const filled = maxSentences == null ? max : Math.max(1, Math.min(maxSentences, max));
  return (
    <div className="aiv-lenbar">
      <div className="aiv-lenbar__track">
        {Array.from({ length: max }).map((_, i) => <span key={i} className={`aiv-lenbar__cell${i < filled ? ' is-on' : ''}`} />)}
      </div>
      <span className="aiv-lenbar__lbl">{maxSentences == null ? '무제한' : `${maxSentences}문장`}</span>
    </div>
  );
}

/** 출력형식 배지. */
export function OutputBadge({ outputFormat }: { outputFormat?: string }) {
  const of = outputFormat ?? 'markdown';
  const label = of === 'html' ? 'HTML' : of === 'data_table' ? '데이터 표(값 고정)' : 'Markdown';
  return <span className={`aiv-badge aiv-badge--${of}`}>{label}</span>;
}

/** 적용 규칙(지침) 목록. */
export function AppliedRules({ items }: { items: string[] }) {
  if (!items.length) return <p className="aiaf-empty">지침 없음</p>;
  return <ul className="aiv-applied">{items.map((s, i) => <li key={i}>{s}</li>)}</ul>;
}

// 포맷 키/엔트리 → 렌더 kind (목업·예제용).
//  - 프리셋 키면 그대로, hr_table→table.
//  - 커스텀/AI 생성(키 미상)이면 출력형식 + 설명/지침 키워드로 모양을 추론(목록·리포트·서술·표).
const KNOWN_MOCK = new Set(['table', 'list', 'report', 'detailed_para', 'data_table', 'diagram', 'gantt', 'mindmap', 'sequence', 'pie']);
export function formatKind(key: string | undefined, entry?: SynthesisFormatEntry): string {
  if (key && key in FORMAT_PRESET_BY_KEY) return key;
  if (key === 'hr_table') return 'table';
  // [신뢰] AI/운영자가 지정한 시각 모양이 있으면 그대로(추론보다 우선).
  if (entry?.mock && KNOWN_MOCK.has(entry.mock)) return entry.mock;
  const of = entry?.output_format;
  // markdown 계열 — 설명/지침 텍스트로 세부 모양 추정. (다이어그램 우선 — output_format=markdown 이라 가려지지 않게)
  const text = [entry?.description ?? '', ...(entry?.instructions ?? [])].join(' ');
  if (/마인드맵|마인드 맵|mindmap|mind\s?map|생각\s?정리|브레인스토밍/i.test(text)) return 'mindmap';
  if (/시퀀스|sequence|순차도|상호작용|메시지\s?흐름|메시지\s?순서/i.test(text)) return 'sequence';
  if (/파이\s?차트|pie|원형\s?차트|비율|구성비|점유율|도넛|donut/i.test(text)) return 'pie';
  if (/간트|gantt|타임라인|timeline|일정표|스케줄/i.test(text)) return 'gantt';
  if (/흐름도|순서도|관계도|다이어그램|diagram|mermaid|\bDAG\b|flowchart|graph|노드|간선/i.test(text)) return 'diagram';
  if (of === 'data_table') return 'data_table';
  if (of === 'html') return 'report';
  if (/리포트|보고서|report|제목.*요약/i.test(text)) return 'report';
  if (/목록|불릿|리스트|list|bullet/i.test(text)) return 'list';
  if (/서술|문단|paragraph|자세히|상세히/i.test(text)) return 'detailed_para';
  if (/표|table/i.test(text)) return 'table';
  // 엔트리 정보가 없으면(키도 엔트리도 미상) 일반 텍스트, 아니면 표 기본.
  return entry ? 'table' : 'plain';
}

// 도메인별 샘플 콘텐츠 (결합 예제용).
interface DomainSample { topic: string; cols: string[]; rows: string[][]; items: string[]; para: string; reportSub: string }
const DOMAIN_SAMPLE: Record<string, DomainSample> = {
  calendar: { topic: '다음 주 일정', cols: ['일정', '시간', '장소'],
    rows: [['팀 회의', '월 10:00', '3F 회의실'], ['고객 미팅', '수 14:00', '온라인'], ['회고', '금 16:00', '5F 라운지']],
    items: ['월 10:00 팀 주간 회의', '수 14:00 고객사 미팅', '금 16:00 스프린트 회고'],
    para: '다음 주에는 총 3건의 일정이 있습니다. 월요일 팀 회의, 수요일 고객 미팅, 금요일 회고 순으로 진행됩니다.',
    reportSub: '다음 주 일정 3건 · 외부 미팅 1건 포함.' },
  hr: { topic: '직원 정보', cols: ['항목', '값'],
    rows: [['사원번호', '10293'], ['이름', '홍길동'], ['부서', '영업팀'], ['직책', '과장']],
    items: ['홍길동 · 영업팀 과장', '입사 2021-03-02', '사번 10293'],
    para: '홍길동 과장은 영업팀 소속으로 2021년 3월에 입사했습니다.',
    reportSub: '영업팀 · 과장 · 재직 중.' },
  chat: { topic: '최근 대화', cols: ['작성자', '시간', '내용'],
    rows: [['김영희', '10:02', '내일 회의 자료 공유드려요'], ['이철수', '10:05', '확인했습니다']],
    items: ['김영희 10:02 — 자료 공유', '이철수 10:05 — 확인'],
    para: '최근 대화에서 김영희님이 자료를 공유했고 이철수님이 확인했습니다.',
    reportSub: '최근 메시지 2건.' },
  minutes: { topic: '회의록', cols: ['항목', '내용'],
    rows: [['제목', '주간 스프린트'], ['일자', '2026-06-04'], ['결정', '핫픽스 우선'], ['담당', '홍길동']],
    items: ['안건: 스프린트 결과 공유', '결정: 핫픽스 우선 처리', '액션: 홍길동 QA(수까지)'],
    para: '주간 스프린트 회의에서 배포 지연을 논의했고, 핫픽스를 우선 처리하기로 결정했습니다.',
    reportSub: '주간 스프린트 · 결정 1건 · 액션 1건.' },
  wiki: { topic: '사내 정책', cols: ['항목', '내용'],
    rows: [['문서', '연차 규정'], ['핵심', '연 15일'], ['신청', '3일 전']],
    items: ['연차 연 15일', '사용 3일 전 신청', '팀장 승인 필요'],
    para: '연차는 연 15일이며, 사용 3일 전에 신청하여 팀장 승인을 받아야 합니다.',
    reportSub: '연차 규정 요약.' },
  default: { topic: '결과', cols: ['항목', '값'], rows: [['항목 A', '값 1'], ['항목 B', '값 2']],
    items: ['항목 A', '항목 B'], para: '요청하신 내용을 아래와 같이 정리했습니다.', reportSub: '요약.' },
};
function sampleFor(domain: string): DomainSample { return DOMAIN_SAMPLE[domain] ?? DOMAIN_SAMPLE['default']!; }

function introText(topic: string, formality?: string): string {
  if (formality === 'formal') return `요청하신 ${topic}을(를) 확인하였습니다. 아래와 같이 안내드립니다.`;
  if (formality === 'friendly') return `네! ${topic} 정리해 드릴게요.`;
  return `${topic}는 아래와 같습니다.`;
}

/** 구조(포맷) 본문 렌더 — 도메인 콘텐츠를 해당 포맷 kind 로. */
function StructureBody({ domain, kind }: { domain: string; kind: string }) {
  const s = sampleFor(domain);
  switch (kind) {
    case 'list':
      return <ul>{s.items.map((it, i) => <li key={i}>{it}</li>)}</ul>;
    case 'detailed_para':
      return <><p>{s.para}</p><p>자세한 사항이 필요하면 말씀해 주세요.</p></>;
    case 'report':
      return <><p className="s-title">{s.topic} 리포트</p><p className="s-sub">요약 — {s.reportSub}</p><p>{s.para}</p></>;
    case 'data_table':
      return <><p>{`(값 고정·결정적 — LLM 합성 없이 항상 동일 표)`}</p><table><thead><tr>{s.cols.map((c) => <th key={c}>{c}</th>)}</tr></thead>
        <tbody>{s.rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>)}</tbody></table></>;
    case 'table':
    default:
      return <table><thead><tr>{s.cols.map((c) => <th key={c}>{c}</th>)}</tr></thead>
        <tbody>{s.rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>)}</tbody></table>;
  }
}

/** 톤 단독 시각화 + 말투 예제. */
export function ToneVizSample({ tone }: { tone?: SynthesisToneEntry }) {
  return (
    <div>
      <div className="aiv-row"><span className="aiv-row__lbl">격식</span><FormalityGauge formality={tone?.formality} /></div>
      <div className="aiv-row"><span className="aiv-row__lbl">길이</span><LengthBar maxSentences={tone?.max_sentences} /></div>
      <div className="aiaf-detail__sec">
        <div className="aiaf-detail__sec-lbl">말투 예제</div>
        <div className="aiv-example">
          <div className="aiv-example__bar"><span className="aiv-example__dot" />AI 답변 미리보기</div>
          <div className="aiv-example__body"><ToneSample formality={tone?.formality} /></div>
          <div className="aiv-example__cap">같은 내용을 이 톤(말투)으로 답할 때의 어조입니다. 구조·표시형식은 포맷이 결정합니다.</div>
        </div>
      </div>
    </div>
  );
}

/** 포맷 단독 시각화 + 구조 예제. */
// 포맷 → 예제 색상 CSS 변수: 팔레트(--p1..--p6) + 외부보더(--pb) + 내부보더(--pbi).
// 예제 요소가 var(--pN, 기본)로 색을 받음. (미리보기 어디서든 동일 적용)
export function formatStyleVars(format?: SynthesisFormatEntry): CSSProperties | undefined {
  if (!format) return undefined;
  const o: Record<string, string> = {};
  (format.palette ?? []).slice(0, 6).forEach((c, i) => { o[`--p${i + 1}`] = c; });
  if (format.borderColor) o['--pb'] = format.borderColor;
  if (format.innerBorderColor) o['--pbi'] = format.innerBorderColor;
  return Object.keys(o).length ? (o as CSSProperties) : undefined;
}

// 색1~5(+보더)가 미리보기에서 어디에 매핑되는지 — 설명/범례용.
export const PALETTE_ROLES: { slot: string; label: string }[] = [
  { slot: '색 1', label: '주요 강조 — 제목·표 헤더·1번째 항목/슬라이스·강조 텍스트' },
  { slot: '색 2', label: '보조 — 2번째 항목/슬라이스·간트 막대' },
  { slot: '색 3', label: '3번째 항목/슬라이스·하이라이트(형광)' },
  { slot: '색 4', label: '4번째 항목(차트·다이어그램)' },
  { slot: '색 5', label: '5번째 항목(차트·다이어그램)' },
];

export function FormatVizSample({ formatKey, format }: { formatKey?: string; format?: SynthesisFormatEntry }) {
  const kind = formatKind(formatKey, format);
  const pal = format?.palette;
  const palStyle = formatStyleVars(format);
  return (
    <div>
      <div className="aiv-row">
        <span className="aiv-row__lbl">아이콘</span>
        <span className="aiv-iconpreview"><FormatMock kind={kind} /></span>
        <span className="aiv-row__lbl" style={{ width: 'auto', marginLeft: 8 }}>출력</span>
        <OutputBadge outputFormat={format?.output_format} />
      </div>
      <div className="aiaf-detail__sec">
        <div className="aiaf-detail__sec-lbl">구조 예제 (AI 답변 미리보기)</div>
        <div className="aiv-example" style={palStyle}>
          <div className="aiv-example__bar">
            <span className="aiv-example__dot" />AI 답변 미리보기
            {pal && pal.length > 0 && (
              <span className="aiv-example__pal">{pal.slice(0, 6).map((c, i) => <i key={i} style={{ background: c }} />)}</span>
            )}
          </div>
          <div className="aiv-example__body"><FormatSample kind={kind} /></div>
          <div className="aiv-example__cap">이 포맷(구조)으로 답변이 표시됩니다{pal && pal.length ? ' (색상 팔레트 반영)' : ''}{format?.borderColor ? ' · 외부 보더 적용' : ''}. 말투·어조는 톤이 결정합니다.</div>
        </div>
      </div>
    </div>
  );
}

/** 결합 예제 — 카테고리에 적용된 톤(말투)×포맷(구조)을 도메인 샘플로 함께 렌더. */
export function CombinedExample({
  domain, tone, formatKey, format, toneLabel, formatLabel,
}: { domain: string; tone?: SynthesisToneEntry; formatKey?: string; format?: SynthesisFormatEntry; toneLabel: string; formatLabel: string }) {
  const s = sampleFor(domain);
  const hasTone = !!tone;
  const hasFmt = !!format;
  const kind = hasFmt ? formatKind(formatKey, format) : 'none';
  return (
    <div>
      <div className="aiv-combo">
        <span className="aiv-chip aiv-chip--tone">톤: {toneLabel}</span>
        <span className="aiv-chip__x">×</span>
        <span className="aiv-chip aiv-chip--format">포맷: {formatLabel}</span>
      </div>
      <div className="aiv-example">
        <div className="aiv-example__bar"><span className="aiv-example__dot" />{DOMAIN_LABELS[domain] ?? domain} 카테고리 — 결합 예시</div>
        <div className="aiv-example__body">
          {hasTone && <p>{introText(s.topic, tone!.formality)}</p>}
          {hasFmt ? <StructureBody domain={domain} kind={kind} /> : <p>{s.para}</p>}
        </div>
        <div className="aiv-example__cap">
          {hasTone || hasFmt
            ? '톤(말투)과 포맷(구조)이 함께 적용된 실제 답변 모양입니다.'
            : '미설정 — 시스템 기본 형식으로 답변합니다.'}
        </div>
      </div>
    </div>
  );
}

// ─── 아이콘+명칭 포맷 드롭다운 (네이티브 select 는 아이콘 불가 → 커스텀) ───
// popup 은 portal(body)+position:fixed 로 렌더 → 모달·스크롤 컨테이너 overflow 에 잘리지 않고 항상 최상단.
export function FormatSelect({ value, formats, onChange, placeholder = '미설정' }: {
  value: string;
  formats: Record<string, SynthesisFormatEntry>;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLUListElement | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number; bottom: number; minWidth: number; above: boolean } | null>(null);

  const recompute = useCallback(() => {
    const b = btnRef.current?.getBoundingClientRect();
    if (!b) return;
    const spaceBelow = window.innerHeight - b.bottom;
    const above = spaceBelow < 300 && b.top > spaceBelow; // 아래 공간 부족하면 위로 플립
    setPos({ left: b.left, top: b.bottom, bottom: window.innerHeight - b.top, minWidth: Math.max(b.width, 240), above });
  }, []);

  useEffect(() => {
    if (!open) return;
    recompute();
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onMove = () => recompute();
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open, recompute]);

  const keys = Object.keys(formats);
  const curLabel = value ? (formats[value]?.description || value) : placeholder;
  const curKind = value ? formatKind(value, formats[value]) : 'none';

  const popup = open && pos ? createPortal(
    <ul ref={popRef} className="aiv-fsel__pop" role="listbox"
      style={{
        position: 'fixed', left: pos.left, minWidth: pos.minWidth, zIndex: 9999,
        ...(pos.above ? { bottom: pos.bottom + 4 } : { top: pos.top + 4 }),
      }}>
      <li role="option" aria-selected={!value} className={`aiv-fsel__opt${!value ? ' is-active' : ''}`}
        onClick={() => { onChange(''); setOpen(false); }}>
        <span className="aiv-fsel__mock"><FormatMock kind="none" /></span>
        <span className="aiv-fsel__opt-lbl">{placeholder}</span>
      </li>
      {keys.map((k) => (
        <li key={k} role="option" aria-selected={k === value} className={`aiv-fsel__opt${k === value ? ' is-active' : ''}`}
          onClick={() => { onChange(k); setOpen(false); }}>
          <span className="aiv-fsel__mock"><FormatMock kind={formatKind(k, formats[k])} /></span>
          <span className="aiv-fsel__opt-lbl">{formats[k]?.description || k}</span>
          <span className="aiv-fsel__opt-of">{formats[k]?.output_format ?? 'markdown'}</span>
        </li>
      ))}
    </ul>,
    document.body,
  ) : null;

  return (
    <div className="aiv-fsel">
      <button ref={btnRef} type="button" className="aiv-fsel__btn" aria-haspopup="listbox" aria-expanded={open}
        onClick={() => setOpen((o) => !o)}>
        <span className="aiv-fsel__mock"><FormatMock kind={curKind} /></span>
        <span className="aiv-fsel__lbl">{curLabel}</span>
        <span className="aiv-fsel__chev" aria-hidden>▾</span>
      </button>
      {popup}
    </div>
  );
}

// ─── 색상 팔레트 선택 (포맷별 — 차트·다이어그램·HTML 강조 색) ───
export function PalettePicker({ value, onChange }: { value?: string[]; onChange: (colors: string[] | undefined) => void }) {
  const curKey = value && value.length
    ? (COLOR_PALETTES.find((p) => p.colors.join() === value.join())?.key ?? 'custom')
    : 'none';
  return (
    <div className="aiv-palette">
      <div className="aiv-palette__row">
        <button type="button" className={`aiv-palette__opt${curKey === 'none' ? ' is-sel' : ''}`}
          onClick={() => onChange(undefined)}>
          <span className="aiv-palette__sw aiv-palette__sw--none" aria-hidden />
          <span className="aiv-palette__lbl">없음</span>
        </button>
        {COLOR_PALETTES.map((p) => (
          <button key={p.key} type="button" className={`aiv-palette__opt${curKey === p.key ? ' is-sel' : ''}`}
            onClick={() => onChange([...p.colors])} title={p.label}>
            <span className="aiv-palette__sw">{p.colors.slice(0, 5).map((c, i) => <i key={i} style={{ background: c }} />)}</span>
            <span className="aiv-palette__lbl">{p.label}</span>
          </button>
        ))}
      </div>
      {value && value.length > 0 && (
        <div className="aiv-palette__cur">적용 색상: {value.map((c, i) => <i key={i} style={{ background: c }} title={c} />)}</div>
      )}
    </div>
  );
}
